/**
 * Shared appliance inverter listing contract (Wave 2 appliance inverter Phase 1).
 *
 * Owns: `appliance_inverter_type` enum, legacy subcategory alias → canonical + defaults,
 * title wording helpers. Family modules (AC / washer / microwave) own form + titles.
 */

import type { ListingSpecValidationIssue } from "@/lib/listing/specRequirements";

export const APPLIANCE_INVERTER_TYPES = ["inverter", "non_inverter"] as const;
export type ApplianceInverterType = (typeof APPLIANCE_INVERTER_TYPES)[number];

export const MICROWAVE_SMART_TYPES = ["smart", "non_smart"] as const;
export type MicrowaveSmartType = (typeof MICROWAVE_SMART_TYPES)[number];

export const CANONICAL_AIR_CONDITIONER_SUBCATEGORY_ID =
  "air-conditioner" as const;
export const CANONICAL_WASHING_MACHINE_SUBCATEGORY_ID =
  "washing-machine" as const;
export const CANONICAL_MICROWAVE_SUBCATEGORY_ID = "microwave" as const;

export const LEGACY_INVERTER_AIR_CONDITIONER_SUBCATEGORY_ID =
  "inverter-air-conditioner" as const;
export const LEGACY_INVERTER_WASHING_MACHINE_SUBCATEGORY_ID =
  "inverter-washing-machine" as const;

export const LEGACY_MICROWAVE_SUBCATEGORY_IDS = [
  "inverter-microwave",
  "non-inverter-microwave",
  "smart-microwave",
  "non-smart-microwave",
] as const;

export type LegacyMicrowaveSubcategoryId =
  (typeof LEGACY_MICROWAVE_SUBCATEGORY_IDS)[number];

export type ApplianceInverterFamily = "ac" | "washer" | "microwave";

export type LegacySubcategoryInverterMapping = {
  legacyId: string;
  canonicalId: string;
  family: ApplianceInverterFamily;
  /** Fixed from legacy id when known; otherwise infer from title. */
  applianceInverterType: ApplianceInverterType | "from_title";
  microwaveSmartType?: MicrowaveSmartType | "from_title";
};

/** Locked Phase 0 alias map (SIO + reference backfill). */
export const LEGACY_SUBCATEGORY_INVERTER_MAP: readonly LegacySubcategoryInverterMapping[] =
  [
    {
      legacyId: CANONICAL_AIR_CONDITIONER_SUBCATEGORY_ID,
      canonicalId: CANONICAL_AIR_CONDITIONER_SUBCATEGORY_ID,
      family: "ac",
      applianceInverterType: "non_inverter",
    },
    {
      legacyId: LEGACY_INVERTER_AIR_CONDITIONER_SUBCATEGORY_ID,
      canonicalId: CANONICAL_AIR_CONDITIONER_SUBCATEGORY_ID,
      family: "ac",
      applianceInverterType: "inverter",
    },
    {
      legacyId: CANONICAL_WASHING_MACHINE_SUBCATEGORY_ID,
      canonicalId: CANONICAL_WASHING_MACHINE_SUBCATEGORY_ID,
      family: "washer",
      applianceInverterType: "non_inverter",
    },
    {
      legacyId: LEGACY_INVERTER_WASHING_MACHINE_SUBCATEGORY_ID,
      canonicalId: CANONICAL_WASHING_MACHINE_SUBCATEGORY_ID,
      family: "washer",
      applianceInverterType: "inverter",
    },
    {
      legacyId: "inverter-microwave",
      canonicalId: CANONICAL_MICROWAVE_SUBCATEGORY_ID,
      family: "microwave",
      applianceInverterType: "inverter",
      microwaveSmartType: "from_title",
    },
    {
      legacyId: "non-inverter-microwave",
      canonicalId: CANONICAL_MICROWAVE_SUBCATEGORY_ID,
      family: "microwave",
      applianceInverterType: "non_inverter",
      microwaveSmartType: "from_title",
    },
    {
      legacyId: "smart-microwave",
      canonicalId: CANONICAL_MICROWAVE_SUBCATEGORY_ID,
      family: "microwave",
      applianceInverterType: "from_title",
      microwaveSmartType: "smart",
    },
    {
      legacyId: "non-smart-microwave",
      canonicalId: CANONICAL_MICROWAVE_SUBCATEGORY_ID,
      family: "microwave",
      applianceInverterType: "from_title",
      microwaveSmartType: "non_smart",
    },
  ] as const;

const mapByLegacyId = new Map(
  LEGACY_SUBCATEGORY_INVERTER_MAP.map((row) => [row.legacyId, row]),
);

export function isApplianceInverterType(
  value: unknown,
): value is ApplianceInverterType {
  return (
    typeof value === "string" &&
    (APPLIANCE_INVERTER_TYPES as readonly string[]).includes(value)
  );
}

export function isMicrowaveSmartType(
  value: unknown,
): value is MicrowaveSmartType {
  return (
    typeof value === "string" &&
    (MICROWAVE_SMART_TYPES as readonly string[]).includes(value)
  );
}

export function resolveCanonicalApplianceSubcategoryId(
  subcategoryId: string | null | undefined,
): string | null {
  if (!subcategoryId) return null;
  const mapped = mapByLegacyId.get(subcategoryId);
  if (mapped) return mapped.canonicalId;
  if (subcategoryId === CANONICAL_MICROWAVE_SUBCATEGORY_ID) {
    return CANONICAL_MICROWAVE_SUBCATEGORY_ID;
  }
  return null;
}

export function getLegacySubcategoryInverterMapping(
  subcategoryId: string | null | undefined,
): LegacySubcategoryInverterMapping | null {
  if (!subcategoryId) return null;
  return mapByLegacyId.get(subcategoryId) ?? null;
}

/**
 * Infer inverter type from legacy subcategory id.
 * Returns null when the id does not encode inverter (caller may use title).
 */
export function inferInverterTypeFromLegacySubcategoryId(
  subcategoryId: string | null | undefined,
): ApplianceInverterType | null {
  const mapped = getLegacySubcategoryInverterMapping(subcategoryId);
  if (!mapped) return null;
  if (mapped.applianceInverterType === "from_title") return null;
  return mapped.applianceInverterType;
}

export function inferMicrowaveSmartTypeFromLegacySubcategoryId(
  subcategoryId: string | null | undefined,
): MicrowaveSmartType | null {
  const mapped = getLegacySubcategoryInverterMapping(subcategoryId);
  if (!mapped || mapped.microwaveSmartType === undefined) return null;
  if (mapped.microwaveSmartType === "from_title") return null;
  return mapped.microwaveSmartType;
}

const NON_INVERTER_TITLE_RE = /\bnon[-\s]?inverter\b/i;
const INVERTER_TITLE_RE = /\binverter\b|dual\s*inverter/i;

/**
 * Strong title cue for the inverter axis. Null when the title does not name it.
 * Does not invent non-inverter from silence (unlike {@link inferInverterTypeFromTitle}).
 */
export function applianceInverterTitleCue(
  title: string | null | undefined,
): ApplianceInverterType | null {
  const t = String(title ?? "");
  if (!t.trim()) return null;
  if (NON_INVERTER_TITLE_RE.test(t)) return "non_inverter";
  if (INVERTER_TITLE_RE.test(t)) return "inverter";
  return null;
}

/** Title / reference inference when legacy id does not encode inverter. */
export function inferInverterTypeFromTitle(
  title: string | null | undefined,
): ApplianceInverterType {
  const t = String(title ?? "").toLowerCase();
  if (/\binverter\b/.test(t) || /dual\s*inverter/.test(t)) {
    return "inverter";
  }
  return "non_inverter";
}

/** Title inference when legacy id does not encode smart. Default non_smart. */
export function inferMicrowaveSmartTypeFromTitle(
  title: string | null | undefined,
): MicrowaveSmartType {
  const t = String(title ?? "").toLowerCase();
  if (
    /\bsmart\b/.test(t) ||
    /\bwifi\b/.test(t) ||
    /\bwi-?fi\b/.test(t) ||
    /\bapp\b/.test(t)
  ) {
    return "smart";
  }
  return "non_smart";
}

export function resolveInverterTypeForLegacySubcategory(
  subcategoryId: string,
  title?: string | null,
): ApplianceInverterType {
  return (
    inferInverterTypeFromLegacySubcategoryId(subcategoryId) ??
    inferInverterTypeFromTitle(title)
  );
}

export function resolveMicrowaveSmartTypeForLegacySubcategory(
  subcategoryId: string,
  title?: string | null,
): MicrowaveSmartType {
  return (
    inferMicrowaveSmartTypeFromLegacySubcategoryId(subcategoryId) ??
    inferMicrowaveSmartTypeFromTitle(title)
  );
}

/**
 * Axis / filter label for inverter type (may include "Non-Inverter").
 * For buyer-facing titles use {@link inverterTitleToken} from
 * `listingTitleTokens` (positive-only; D6).
 */
export function formatInverterTitleWord(
  value: ApplianceInverterType,
): "Inverter" | "Non-Inverter" {
  return value === "inverter" ? "Inverter" : "Non-Inverter";
}

/**
 * Axis / filter label for microwave smart type (may include "Non-Smart").
 * For buyer-facing titles use {@link smartTitleToken} from
 * `listingTitleTokens` (positive-only; D6).
 */
export function formatMicrowaveSmartTitleWord(
  value: MicrowaveSmartType,
): "Smart" | "Non-Smart" {
  return value === "smart" ? "Smart" : "Non-Smart";
}

export function pushApplianceInverterRequiredIssue(
  issues: ListingSpecValidationIssue[],
  message = "Choose inverter or non-inverter.",
): void {
  issues.push({
    key: "appliance_inverter_type",
    code: "required",
    message,
  });
}

export function pushApplianceInverterInvalidIssue(
  issues: ListingSpecValidationIssue[],
  message = "Inverter type must be inverter or non-inverter (unknown is not allowed).",
): void {
  issues.push({
    key: "appliance_inverter_type",
    code: "invalid_enum",
    message,
  });
}
